import { useCallback, useEffect, useId, useState } from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { Label } from 'cpool-components/general/Label';
import { DropdownMenu } from 'cpool-components/input/DropdownMenu';
import {
    APPLICANT_ATTRIBUTIONS,
    CUSTOMER_DATA_NOTIFICATIONS,
    CUSTOMER_DATA_TABS,
    LIVING_SITUATIONS,
    LOAN_REQUEST_COMMANDS,
} from 'cpool-constants';
import { objectKeys } from 'cpool-utils';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField } from '~/features/leadDetails/components/Overview/CustomerData/shared/inputs/FormField';
import { useCustomerDataTabsContext } from '~/features/leadDetails/context/CustomerDataTabsContext.tsx';
import { useLeadDetailsFormContext } from '~/features/leadDetails/context/LeadDetailsFormContext.tsx';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import type { SchemaLivingSituationForm } from '~/features/leadDetails/schemas/living.schema.ts';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

type WohnartProps = {
    antragsteller: 1 | 2;
};

const Wohnart: React.FC<WohnartProps> = ({ antragsteller }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { t } = useTranslation();
    const { control, getValues } = useFormContext<SchemaLivingSituationForm>();
    const labelId = useId();
    const {
        hasSelfOccupiedRealEstateApplicant1,
        hasSelfOccupiedRealEstateApplicant2,
        hasSelfOccupiedRealEstateShared,
        isLivingInSharedHousehold,
        setIsLivingInOwnHouseApplicant1,
        setIsLivingInOwnHouseApplicant2,
    } = useLeadDetailsFormContext();

    const { removeNotification, setNotification } = useCustomerDataTabsContext();

    const wohnart = useWatch({
        name: `antragsteller${antragsteller}.wohnsituation.wohnart`,
    }) as SchemaLivingSituationForm['antragsteller1']['wohnsituation']['wohnart'];

    const [hasNoSelfOccupiedRealEstateError, setHasNoSelfOccupiedRealEstateError] = useState(false);
    const [shouldBeLivingInOwnHouseError, setShouldBeLivingInOwnHouseError] = useState(false);

    const hasSelfOccupiedRealEstate = useCallback(
        (antragstellerNr: 1 | 2) => {
            if (antragstellerNr === 1) return hasSelfOccupiedRealEstateApplicant1;
            if (antragstellerNr === 2) return hasSelfOccupiedRealEstateApplicant2;
            return false;
        },
        [hasSelfOccupiedRealEstateApplicant1, hasSelfOccupiedRealEstateApplicant2],
    );

    const setIsLivingInOwnHouse = useCallback(
        (antragstellerNr: 1 | 2, value: boolean) => {
            if (antragstellerNr === 1) setIsLivingInOwnHouseApplicant1(value);
            if (antragstellerNr === 2) setIsLivingInOwnHouseApplicant2(value);
        },
        [setIsLivingInOwnHouseApplicant1, setIsLivingInOwnHouseApplicant2],
    );

    useEffect(() => {
        const setRealEstateError = (isError: boolean) => {
            const args = {
                notification: CUSTOMER_DATA_NOTIFICATIONS.LIVING_SITUATION.HAS_NO_SELF_OCCUPIED_REAL_ESTATE,
                prefix:
                    antragsteller === 1
                        ? APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1
                        : APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2,
                tab: CUSTOMER_DATA_TABS.LIVING_SITUATION,
            };

            if (isError) setNotification(args);
            else removeNotification(args);

            setHasNoSelfOccupiedRealEstateError(isError);
        };

        const setLivingSituationError = (isError: boolean) => {
            const args = {
                notification: CUSTOMER_DATA_NOTIFICATIONS.LIVING_SITUATION.SHOULD_BE_LIVING_IN_OWN_HOUSE,
                prefix:
                    antragsteller === 1
                        ? APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_1
                        : APPLICANT_ATTRIBUTIONS.ANTRAGSTELLER_2,
                tab: CUSTOMER_DATA_TABS.LIVING_SITUATION,
            };

            if (isError) setNotification(args);
            else removeNotification(args);

            setShouldBeLivingInOwnHouseError(isError);
        };

        // set the aappropriate state in the context provider
        if (wohnart === 'IM_EIGENEN_HAUS') setIsLivingInOwnHouse(antragsteller, true);
        else setIsLivingInOwnHouse(antragsteller, false);

        // when both applicants live in the same house and wohnart is set to IM_EIGENEN_HAUSE
        // there should be a self occupied real estate for one of the applicants or a shared one
        if (
            isLivingInSharedHousehold &&
            wohnart === 'IM_EIGENEN_HAUS' &&
            !hasSelfOccupiedRealEstate(1) &&
            !hasSelfOccupiedRealEstate(2) &&
            !hasSelfOccupiedRealEstateShared
        ) {
            setRealEstateError(true);
            setLivingSituationError(false);
            return;
        }

        // when both applicants live in the same house and wohnart is set to a value other than IM_EIGENEN_HAUSE
        // there should not be a self occupied real estate for any applicant nor a shared one
        if (
            isLivingInSharedHousehold &&
            wohnart !== null &&
            wohnart !== 'IM_EIGENEN_HAUS' &&
            (hasSelfOccupiedRealEstate(1) || hasSelfOccupiedRealEstate(2) || hasSelfOccupiedRealEstateShared)
        ) {
            setLivingSituationError(true);
            setRealEstateError(false);
            return;
        }

        // when the applicants live in different houses and wohnart is set to IM_EIGENEN_HAUSE
        // there should be a self occupied real estate for this applicant
        if (!isLivingInSharedHousehold && wohnart === 'IM_EIGENEN_HAUS' && !hasSelfOccupiedRealEstate(antragsteller)) {
            setRealEstateError(true);
            setLivingSituationError(false);
            return;
        }

        // when the applicants live in different houses and wohnart is set to a value other than IM_EIGENEN_HAUSE
        // there should not be a self occupied real estate for this applicant
        if (
            !isLivingInSharedHousehold &&
            wohnart !== null &&
            wohnart !== 'IM_EIGENEN_HAUS' &&
            hasSelfOccupiedRealEstate(antragsteller)
        ) {
            setLivingSituationError(true);
            setRealEstateError(false);
            return;
        }
        setRealEstateError(false);
        setLivingSituationError(false);
    }, [
        antragsteller,
        getValues,
        hasSelfOccupiedRealEstate,
        hasSelfOccupiedRealEstateApplicant1,
        hasSelfOccupiedRealEstateApplicant2,
        hasSelfOccupiedRealEstateShared,
        isLivingInSharedHousehold,
        removeNotification,
        setIsLivingInOwnHouse,
        setIsLivingInOwnHouseApplicant1,
        setIsLivingInOwnHouseApplicant2,
        setNotification,
        wohnart,
    ]);

    const options = objectKeys(LIVING_SITUATIONS).map((value) => ({
        label: t(`LIVING_SITUATION_DATA.LIVING_TYPE.${value}`),
        value,
    }));

    return (
        <FormField<SchemaLivingSituationForm, keyof typeof LIVING_SITUATIONS | null>
            control={control}
            name={`antragsteller${antragsteller}.wohnsituation.wohnart`}
        >
            {({ error, onChange, value }) => (
                <div className="flex items-start gap-2.5">
                    <Label className="h-8 w-[175px] shrink-0 justify-start" id={labelId}>
                        {t('LIVING_SITUATION_DATA.LIVING_TYPE.TITLE')}
                    </Label>
                    <div className="flex min-w-0 w-full flex-col gap-1">
                        <DropdownMenu
                            aria-labelledby={labelId}
                            data-testid={`antragsteller${antragsteller}.wohnsituation.wohnart`}
                            options={options}
                            value={value ?? undefined}
                            onChange={(newValue) => onChange(newValue as keyof typeof LIVING_SITUATIONS)}
                            placeholder=""
                            triggerClassName="w-full justify-between"
                            className="w-full"
                            disabled={
                                !canExecuteApplicantCommand({
                                    applicant: antragsteller,
                                    commandName: LOAN_REQUEST_COMMANDS.changeApplicantLivingSituation,
                                    permissions,
                                })
                            }
                            error={error}
                            scrollable
                        />
                        {hasNoSelfOccupiedRealEstateError && (
                            <p className="text-xs px-2 pb-2 text-alert-error">
                                {t('LIVING_SITUATION_DATA.NO_SELF_OCCUPIED_REAL_ESTATE_ERROR')}
                            </p>
                        )}
                        {shouldBeLivingInOwnHouseError && (
                            <p className="text-xs px-2 pb-2 text-alert-error">
                                {t('LIVING_SITUATION_DATA.LIVING_IN_OWN_REAL_ESTATE_ERROR')}
                            </p>
                        )}
                    </div>
                </div>
            )}
        </FormField>
    );
};

export default Wohnart;
